import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { tasksApi } from '../api/tasks';
import toast from 'react-hot-toast';
import { CheckCircle2, Circle, Clock, AlertTriangle, User, Filter, ChevronDown, ExternalLink } from 'lucide-react';

const PRIORITY_COLORS = {
  urgent: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  normal: 'bg-blue-100 text-blue-700',
  low: 'bg-gray-100 text-gray-600',
};

const STATUS_TABS = [
  { key: 'open', label: 'Open', filter: ['pending', 'in_progress'] },
  { key: 'completed', label: 'Completed', filter: ['completed'] },
  { key: 'all', label: 'All', filter: [] },
];

function formatDueDate(dateStr) {
  if (!dateStr) return null;
  const due = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return { text: Math.abs(diffDays) + 'd overdue', cls: 'text-red-600 font-medium' };
  if (diffDays === 0) return { text: 'Due today', cls: 'text-orange-600 font-medium' };
  if (diffDays === 1) return { text: 'Due tomorrow', cls: 'text-orange-500' };
  if (diffDays <= 7) return { text: 'Due in ' + diffDays + 'd', cls: 'text-yellow-600' };
  return { text: due.toLocaleDateString(), cls: 'text-gray-500' };
}

export default function MyTasks() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('open');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const tab = STATUS_TABS.find(t => t.key === activeTab);
      const params = { assigned_to: 'me', per_page: 100 };
      if (tab.filter.length === 1) params.status = tab.filter[0];
      if (priorityFilter) params.priority = priorityFilter;
      if (categoryFilter) params.category = categoryFilter;

      const [tasksRes, summaryRes] = await Promise.all([
        tasksApi.list(params),
        tasksApi.summary(),
      ]);

      let filtered = tasksRes.data.tasks;
      if (tab.filter.length > 1) {
        filtered = filtered.filter(t => tab.filter.includes(t.status));
      }
      setTasks(filtered);
      setSummary(summaryRes.data.summary);
    } catch (err) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [activeTab, priorityFilter, categoryFilter]);

  const handleComplete = async (taskId) => {
    try {
      await tasksApi.complete(taskId);
      toast.success('Task completed');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to complete task');
    }
  };

  const handleReopen = async (taskId) => {
    try {
      await tasksApi.reopen(taskId);
      toast.success('Task reopened');
      fetchData();
    } catch (err) {
      toast.error('Failed to reopen task');
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">My Tasks</h1>
          <p className="text-sm text-gray-500">Tasks assigned to you across all records</p>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-2xl font-bold text-gray-900">{summary.pending}</p>
            <p className="text-xs text-gray-500">Pending</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-2xl font-bold text-blue-600">{summary.in_progress}</p>
            <p className="text-xs text-gray-500">In Progress</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-2xl font-bold text-red-600">{summary.overdue}</p>
            <p className="text-xs text-gray-500">Overdue</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-2xl font-bold text-orange-500">{summary.due_this_week}</p>
            <p className="text-xs text-gray-500">Due This Week</p>
          </div>
        </div>
      )}

      {/* Tab bar + filters */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-1 border-b border-gray-200">
          {STATUS_TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={'px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ' +
                (activeTab === tab.key ? 'border-brand-600 text-brand-600' : 'border-transparent text-gray-500 hover:text-gray-700')}>
              {tab.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)}
            className="text-xs border border-gray-200 rounded-md px-2 py-1.5">
            <option value="">All Priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="normal">Normal</option>
            <option value="low">Low</option>
          </select>
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
            className="text-xs border border-gray-200 rounded-md px-2 py-1.5">
            <option value="">All Categories</option>
            <option value="admin">Administrative</option>
            <option value="medical">Medical</option>
            <option value="legal">Legal</option>
            <option value="investigation">Investigation</option>
            <option value="client_comm">Client Communication</option>
            <option value="discovery">Discovery</option>
            <option value="settlement">Settlement</option>
            <option value="general">General</option>
          </select>
        </div>
      </div>

      {/* Task list */}
      <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-50">
        {loading ? (
          <p className="p-8 text-sm text-gray-400 text-center">Loading tasks...</p>
        ) : tasks.length === 0 ? (
          <p className="p-8 text-sm text-gray-400 text-center">No tasks found</p>
        ) : (
          tasks.map(task => {
            const isCompleted = task.status === 'completed';
            const due = formatDueDate(task.due_date);
            const isOverdue = due && due.cls.includes('red') && !isCompleted;
            return (
              <div key={task.id} className={'flex items-start gap-3 px-4 py-3 hover:bg-gray-50 ' + (isCompleted ? 'opacity-60' : '')}>
                <button onClick={() => isCompleted ? handleReopen(task.id) : handleComplete(task.id)} className="mt-0.5 flex-shrink-0">
                  {isCompleted
                    ? <CheckCircle2 className="h-5 w-5 text-green-500" />
                    : <Circle className={'h-5 w-5 ' + (isOverdue ? 'text-red-400' : 'text-gray-300 hover:text-brand-500')} />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={'text-sm ' + (isCompleted ? 'line-through text-gray-400' : 'text-gray-900')}>{task.title}</span>
                    {task.source === 'llm_generated' && <span className="text-xs bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded font-medium">AI</span>}
                    {task.source === 'template' && <span className="text-xs bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded font-medium">TPL</span>}
                  </div>
                  {/* Record link */}
                  {task.record_title && (
                    <button onClick={() => navigate('/records/' + task.record_type_slug + '/' + task.record_id)}
                      className="text-xs text-brand-600 hover:text-brand-700 flex items-center gap-1 mt-0.5">
                      <ExternalLink className="h-3 w-3" /> {task.record_reference} — {task.record_title}
                    </button>
                  )}
                  <div className="flex items-center gap-2 mt-1">
                    <span className={'text-xs px-1.5 py-0.5 rounded ' + (PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.normal)}>{task.priority}</span>
                    {task.category && task.category !== 'general' && (
                      <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{task.category}</span>
                    )}
                    {due && <span className={'text-xs flex items-center gap-1 ' + due.cls}><Clock className="h-3 w-3" />{due.text}</span>}
                    {isOverdue && <AlertTriangle className="h-3 w-3 text-red-500" />}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
